import { HttpClient } from '@angular/common/http';
import { ErrorHandler, Injectable } from '@angular/core';
import { UserInfo } from '../../shared/models/user-info.model';
import { BusinessAdminNote } from '../models/business-admin-notes.model';
import { AuthEventType, IAuthEvent } from '../../auth/models/auth-event.model';
import { AuthService } from '../../auth/auth/auth.service';
import { UserService } from '../../shared/user/user.service';
import { environment } from '../../../environments/environment';
import { BusinessManagerService } from '../../shared/user/business-manager.service';
import { BusinessAdminNotesDates } from '../models/business-admin-notes-dates';


@Injectable()
export class BusinessAdminNotesService {

  userInfo: UserInfo;

  constructor(private _http: HttpClient,
    private _auth: AuthService,
    private _userService: UserService,
    private _errorHandler: ErrorHandler,
    private _businessManagerService: BusinessManagerService) {
    this._auth.authStream.subscribe(e => this._onAuthEvent(e));
  }

  private async _onAuthEvent(e: IAuthEvent): Promise<void> {
    if (!e) {
      return;
    }
    switch (e.type) {
      case AuthEventType.SignIn:
        this.userInfo = await this._userService.getUserInfo();
        break;
      case AuthEventType.InvalidSignIn:
      case AuthEventType.SignOut:
        this.userInfo = null;
        break;
    }
  }


  /**
   * Gets all the businessAdminNotes based on the businessID from the API.
   * Requires that user is a flight deck admin
   * @param businessId {number} Business ID
   * @returns {Promise<Array<Business>>} BusinessAdminNotes
   */
  async getBusinessAdminNotes(businessId: number): Promise<Array<BusinessAdminNote>> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser ||
        !(await this._businessManagerService.isFlightDeckAdmin() ||
        await this._businessManagerService.isAmbassador()) ) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/${businessId}/adminNotes`;
    try {
      const RESPONSE = await this._http.get<any>(URL).toPromise();
      const BUSINESS_ADMIN_NOTES_DATES = BusinessAdminNotesDates.parse(RESPONSE);
      if (!BUSINESS_ADMIN_NOTES_DATES) {
        const ERROR = new Error('API did not return valid business admin notes object');
        this._errorHandler.handleError(ERROR);
        throw ERROR;
      } else {
        return BUSINESS_ADMIN_NOTES_DATES.notes;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Method creates a new businessAdminNote in the API Server via a post request with the string, note.
   * Requires that user is a flight deck admin
   * @param businessId {number} used to identify the business
   * @param note {string} contents for the note
   * @returns {Promise<BusinessAdminNote>} API Server will respond with a new Business OBJ.
   */
  async createBusinessAdminNote(businessId: number, note: string): Promise<BusinessAdminNote> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser ||
        !(await this._businessManagerService.isFlightDeckAdmin() ||
        await this._businessManagerService.isAmbassador()) ) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/${businessId}/adminNote/`;
    try {
      const RESPONSE = await this._http.post(URL, { note: note }).toPromise();
      const BUSINESS_ADMIN_NOTE = BusinessAdminNote.parse(RESPONSE);
      if (!BUSINESS_ADMIN_NOTE) {
        const ERROR = new Error('API did not return valid business admin note object');
        this._errorHandler.handleError(ERROR);
        throw ERROR;
      } else {
        return BUSINESS_ADMIN_NOTE;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Sends put request to api server to update a given business admin note.
   * Method updates the API Server with data from the json object with one field of note being passed in.
   * Requires that user is a flight deck admin
   * @param businessId {number} businessId
   * @param noteId {number} the note that will be updated
   * @param note {string} content to be updated.
   * @returns {Promise<BusinessAdminNote>} API Server will respond with updated Business OBJ.
   */
  async updateBusinessAdminNote(businessId: number, noteId: number, note: string): Promise<BusinessAdminNote> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser ||
        !(await this._businessManagerService.isFlightDeckAdmin() ||
        await this._businessManagerService.isAmbassador()) ) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/${businessId}/adminNote/${noteId}`;
    try {
      const RESPONSE = await this._http.put(URL, { note: note }).toPromise();
      const BUSINESS_ADMIN_NOTE = BusinessAdminNote.parse(RESPONSE);
      if (!BUSINESS_ADMIN_NOTE) {
        const ERROR = new Error('API did not return valid business admin note object');
        this._errorHandler.handleError(ERROR);
        throw ERROR;
      } else {
        return BUSINESS_ADMIN_NOTE;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Method to call API to delete a business admin note from a business
   * Requires that user is a flight deck admin to delete notes
   * @param businessId {number} the business to remove the admin from.
   * @param noteId {number} the specific note to be deleted as an admin of this business.
   * @returns {Promise<string>} Returns SUCCESS or error message for display to user.
   */
  async deleteBusinessAdminNote(businessId: number, noteId: number): Promise<boolean> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser ||
        !(await this._businessManagerService.isFlightDeckAdmin() ||
        await this._businessManagerService.isAmbassador()) ) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/${businessId}/adminNote/${noteId}`;
    try {
      const RESPONSE = await this._http.delete(URL).toPromise();
      return true;
    } catch (e) {
      this._errorHandler.handleError(e);
      throw false;
    }
  }

  /**
   * Gets all the businessAdminNotes based on the businessID from the API.
   * Requires that user is a flight deck admin
   * @param businessId {number} Business ID
   * @returns {Promise<Array<Business>>} BusinessAdminNotes
   */
  async getBusinessAdminDates(businessId: number): Promise<Array<BusinessAdminNote>> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser ||
      !await this._businessManagerService.isFlightDeckAdmin()) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/${businessId}/adminNotes`;
    try {
      const RESPONSE = await this._http.get<any>(URL).toPromise();
      const BUSINESS_ADMIN_NOTES_DATES = BusinessAdminNotesDates.parse(RESPONSE);
      if (!BUSINESS_ADMIN_NOTES_DATES) {
        const ERROR = new Error('API did not return valid business admin notes object');
        this._errorHandler.handleError(ERROR);
        throw ERROR;
      } else {
        return BUSINESS_ADMIN_NOTES_DATES.changeLog;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

}
